import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Activity, AlertTriangle, DollarSign, Cpu, HardDrive, MemoryStick, Plus, ChevronRight } from 'lucide-react';
import { getSchoolOverview, getSchools, getSystemStats } from '../api';

export default function Overview() {
  const [overview, setOverview] = useState(null);
  const [schools, setSchools] = useState([]);
  const [system, setSystem] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      getSchoolOverview().then(r => setOverview(r.data)),
      getSchools().then(r => setSchools(r.data.results || r.data)),
      getSystemStats().then(r => setSystem(r.data)).catch(() => {}),
    ]);
  }, []);

  const getBarColor = (pct) => pct > 80 ? 'red' : pct > 60 ? 'yellow' : 'green';

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Wyzcon Infrastructure Overview</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card accent">
          <div className="stat-icon accent"><Server size={22} /></div>
          <div className="stat-label">Total Schools</div>
          <div className="stat-value accent">{overview?.total_schools || 0}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon green"><Activity size={22} /></div>
          <div className="stat-label">Active</div>
          <div className="stat-value green">{overview?.active || 0}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-icon red"><AlertTriangle size={22} /></div>
          <div className="stat-label">Issues</div>
          <div className="stat-value red">{overview?.error || 0}</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-icon blue"><DollarSign size={22} /></div>
          <div className="stat-label">Monthly Revenue</div>
          <div className="stat-value blue">${overview?.monthly_revenue || 0}</div>
        </div>
      </div>

      {system && (
        <div className="card mb-4">
          <div className="card-header">
            <h3><Cpu size={18} /> Server Health</h3>
            <span className="text-sm text-secondary">
              {system.cpu_count || '?'} vCPU · {system.memory_total_gb}GB RAM
            </span>
          </div>
          <div className="card-body">
            <div className="progress-group">
              <div className="progress-header">
                <span className="progress-label">CPU</span>
                <span className="progress-value">{system.cpu_percent}%</span>
              </div>
              <div className="progress-bar">
                <div className={`progress-fill ${getBarColor(system.cpu_percent)}`} style={{ width: `${system.cpu_percent}%` }} />
              </div>
            </div>

            <div className="progress-group">
              <div className="progress-header">
                <span className="progress-label">Memory</span>
                <span className="progress-value">{system.memory_used_gb}GB / {system.memory_total_gb}GB</span>
              </div>
              <div className="progress-bar">
                <div className={`progress-fill ${getBarColor(system.memory_percent)}`} style={{ width: `${system.memory_percent}%` }} />
              </div>
            </div>

            <div className="progress-group">
              <div className="progress-header">
                <span className="progress-label">Disk</span>
                <span className="progress-value">{system.disk_used_gb}GB / {system.disk_total_gb}GB</span>
              </div>
              <div className="progress-bar">
                <div className={`progress-fill ${getBarColor(system.disk_percent)}`} style={{ width: `${system.disk_percent}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3><Server size={18} /> Schools</h3>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/deploy')}>
            <Plus size={15} /> Deploy New
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>School</th>
                <th>Domain</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Monthly</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {schools.map(s => (
                <tr key={s.school_id} className="clickable" onClick={() => navigate(`/schools/${s.school_id}`)}>
                  <td>
                    <strong>{s.name}</strong>
                    <br />
                    <span className="text-muted text-xs">{s.school_id}</span>
                  </td>
                  <td className="font-mono text-sm">{s.domain}</td>
                  <td className="text-sm" style={{ textTransform: 'capitalize' }}>{s.plan}</td>
                  <td><span className={`badge ${s.status}`}>{s.status}</span></td>
                  <td className="font-mono">${s.monthly_price}</td>
                  <td><ChevronRight size={16} className="text-muted" /></td>
                </tr>
              ))}
              {schools.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <Server size={40} />
                      <p>No schools deployed yet</p>
                      <span className="sub">Deploy your first school to get started</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
